import { isSameDay, parseISO, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { css, styled } from 'styled-components';

import type { ApiListingLocationType } from 'api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Icon } from 'components/Icon/Icon';
import { Tag } from 'components/Tag/Tag';
import { ListingLocationLabel } from 'modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from 'modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { colors } from 'theme/theme';
import type { EmploymentType } from 'utils/constants/general/employmentTypes';
import { shorterDate, shorterTime } from 'utils/date';

import {
  SearchHitInfoBoxListingLabelsHolder,
  SearchHitInfoBoxListingLabelsPill,
  SearchHitInfoBoxListingLabelsPillContainer,
} from './SearchHitInfoBoxListingLabels.styled';

type Props = {
  listingType: 'ORG' | 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT' | 'IMPORTED';
  locationType?: ApiListingLocationType;
  location?: string;
  jobType?: EmploymentType[];
  salaryFormatted?: string;
  timezone?: string | null;
  startDateTime?: ApiISODateTimeString | null;
  endDateTime?: ApiISODateTimeString | null;
  pillItems?: string[];
};

const TagRowContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: row wrap;
  gap: 8px;

  &:empty {
    display: none;
  }
`;

const StyledTag = styled(Tag)<{ $thickLeftBorder?: boolean }>`
  ${({ $thickLeftBorder }) =>
    $thickLeftBorder &&
    css`
      border-left: 5px solid ${colors.elementGrey};
    `};
`;

const formatDate = (
  startDateTime?: ApiISODateTimeString | null,
  endDateTime?: ApiISODateTimeString | null,
  timezone?: string | null,
) => {
  // 1/12/2024 - 4/02/2025
  if (
    startDateTime &&
    endDateTime &&
    !isSameDay(endDateTime, startDateTime) &&
    startOfDay(startDateTime).getTime() === parseISO(startDateTime).getTime() &&
    startOfDay(endDateTime).getTime() === parseISO(endDateTime).getTime()
  ) {
    return `${shorterDate(startDateTime)} - ${shorterDate(endDateTime)}`;
  }

  // 1/12/2024 10:00 AM - 5:00 PM EDT
  if (startDateTime && endDateTime && isSameDay(endDateTime, startDateTime)) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, false)} - ${shorterTime(endDateTime, timezone, true)}`;
  }

  // 1/12/2024 10:00 AM - 4/02/2025 5:00 PM EDT
  if (startDateTime && endDateTime) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, false)} - ${shorterDate(endDateTime)} ${shorterTime(endDateTime, timezone, true)}`;
  }

  // 1/12/2024 10:00 AM EDT
  if (startDateTime && !endDateTime) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, true)}`;
  }
};

export function SearchHitInfoBoxListingLabels({
  listingType,
  location,
  jobType,
  locationType,
  salaryFormatted,
  timezone,
  startDateTime,
  endDateTime,
  pillItems,
}: Props) {
  const jobTypeLabel = useMemo(() => {
    // Note: order is important here.
    if (jobType?.includes('CONTRACT')) return getText('Contract/Freelance');
    if (jobType?.includes('FULL_TIME')) return getText('Full Time');
    if (jobType?.includes('PART_TIME')) return getText('Part Time');
  }, [jobType]);

  return (
    <SearchHitInfoBoxListingLabelsHolder>
      <TagRowContainer>
        {locationType && (
          <ListingLocationTypeLabel
            listingType={listingType}
            locationType={locationType}
          />
        )}

        {location &&
          locationType &&
          (listingType !== 'EVENT' ||
            (listingType === 'EVENT' && locationType !== 'REMOTE')) && (
            <ListingLocationLabel
              locationType={locationType}
              location={location}
            />
          )}
      </TagRowContainer>

      <TagRowContainer>
        {listingType === 'JOB' && (
          <StyledTag size="micro" $thickLeftBorder>
            {jobTypeLabel}
          </StyledTag>
        )}

        {salaryFormatted && (
          <StyledTag size="micro" $thickLeftBorder>
            {salaryFormatted}
          </StyledTag>
        )}

        {(startDateTime || endDateTime) && (
          <StyledTag size="micro" prefix={<Icon name="calendar" size={14} />}>
            {formatDate(startDateTime, endDateTime, timezone)}
          </StyledTag>
        )}
        {pillItems && (
          <SearchHitInfoBoxListingLabelsPillContainer>
            {pillItems.map((pillItem) => (
              <SearchHitInfoBoxListingLabelsPill key={pillItem}>
                {pillItem}
              </SearchHitInfoBoxListingLabelsPill>
            ))}
          </SearchHitInfoBoxListingLabelsPillContainer>
        )}
      </TagRowContainer>
    </SearchHitInfoBoxListingLabelsHolder>
  );
}
